import { FiltrosLote } from '@sotran/lib-shipper';
import { Col, Row } from 'antd';
import PropTypes from 'prop-types';
import React, { useCallback, useEffect, useState } from 'react';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Legend,
  ResponsiveContainer,
  Text,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { useDebouncedCallback } from 'use-debounce';

import SpinDot from '../../../components/SpinDot';
import { flatten } from '../../../helpers/array';
import { getCnpjResumido, getCnpjSecundarios } from '../../../helpers/shipper';
import { useAuth } from '../../../security/authContext';
import apiAnalytics from '../../../services/apiAnalytics';
import apiNetCore from '../../../services/apiNetCore';
import colors from '../../../styles/colors';
import { createLabels } from '../utils/createLabels';
import VerMais from '../VerMais';

function AderenciaCarregamento({ tipo, datas, setDate, tab, width, height }) {
  const { user: userInfo } = useAuth();
  const [bars, setBars] = useState([]);
  const [dataDash, setDataDash] = useState([]);
  const [dashAtual, setDashAtual] = useState([]);
  const [isVerMais, setIsVerMais] = useState(false);
  const [order, setOrder] = useState('');
  const [dataAlterada, setDataAlterada] = useState([]);
  const [yAxis] = useState([0, 20, 40, 60, 80, 100, 120, 140]);
  const [loading, setLoading] = useState(false);
  const [lotesId, setLotesId] = useState([]);
  const colorsBar = [
    colors.$brandcolor_primary_dark_green_5,
    colors.$auxiliary_sky_2,
    colors.$greyscale_dark_grey,
  ];

  const getYearsForLegend = aderencias => {
    const years = aderencias.map(aderencia => aderencia.data.map(d => d.ano));
    const yearsFlatUnique = [...new Set(flatten(years))].sort();

    return yearsFlatUnique.map((year, index) => ({
      year,
      fill: index <= colorsBar.length - 1 ? colorsBar[index] : colorsBar[0],
    }));
  };

  const transformaData = aderencia => {
    const label = [];
    aderencia.forEach(arr => {
      const dados = arr.data.reduce((obj, val) => {
        obj[val.ano] = parseInt(val.value, 10);
        return obj;
      }, {});
      label.push({
        name: arr.label,
        ...dados,
      });
    });

    return label;
  };

  const getAderencia = useCallback(async () => {
    try {
      if (tab === 'aderencia') {
        setLoading(true);

        const listCnpj = [];
        listCnpj.push(getCnpjResumido());

        if (localStorage.getItem('cliente_secundarios')) {
          listCnpj.push(...getCnpjSecundarios());
        }

        const query = {
          type: tipo || 'WEEKLY',
          cnpjs: listCnpj,
          ...(lotesId.length > 0 && { lotesId }),
          ...datas,
        };

        const aderencias = await apiAnalytics.get(
          '/relatorios/aderencia-carregamento',
          {
            params: { ...query },
          }
        );
        const retorno = getYearsForLegend(aderencias.data);

        if (bars.length === 0 || (bars.length === 1 && retorno.length > 1)) {
          setBars(retorno);
        }

        const labels = createLabels(
          datas?.dataInicial,
          datas?.dataFinal,
          dataAlterada?.dataInicial,
          dataAlterada?.dataFinal,
          false,
          tipo
        );

        const dados = labels.map(label => ({
          data: [],
          label,
        }));

        dados.forEach(dado => {
          aderencias.data.forEach(variacao => {
            if (dado.label === variacao.label) {
              dado.data = [...dado.data, ...variacao.data];
            }
          });
        });

        const dataDashResponse = transformaData(dados);
        if (isVerMais && order === 'past') {
          const dashAlterado = dashAtual;
          dashAlterado.pop();
          dashAlterado.unshift(dataDashResponse[0]);
          setDashAtual(dashAlterado);
          setDataDash(dashAlterado);
          setOrder('');
          setIsVerMais(false);
        } else if (isVerMais && order === 'future') {
          const dashAlterado = dashAtual;
          dashAlterado.shift();
          dashAlterado.push(dataDashResponse[0]);

          setDashAtual(dashAlterado);
          setDataDash(dashAlterado);
          setOrder('');
          setIsVerMais(false);
        } else {
          setDataAlterada(datas);
          setDashAtual(dataDashResponse);
          setDataDash(dataDashResponse);
        }
      }
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tipo, datas, tab, lotesId]);

  useEffect(() => {
    getAderencia();
  }, [getAderencia, lotesId]);

  const customLabel = bar => (
    <Text
      x={bar.x + 5}
      y={bar.y - 5}
      fill={colors.$greyscale_dark_grey}
      textAnchor="start"
      verticalAnchor="end"
    >
      {`${bar.value}%`}
    </Text>
  );

  const setStatusLoteCallback = useDebouncedCallback(
    value => {
      setLotesId(value);
    },
    [1000]
  );

  return (
    <>
      <Row>
        <Col xs={6} sm={6} md={6} lg={6}>
          <FiltrosLote
            apiNetCore={apiNetCore}
            clientesPagadores={userInfo.clientes_pagadores || []}
            showStatus={true}
            onChangeCallback={setStatusLoteCallback}
            statusLote={[1, 2, 3, 4]}
            placeholder="Lote"
          />
        </Col>
      </Row>
      <Row
        type="flex"
        align="middle"
        justify="center"
        className={loading ? 'chart-custom-loading' : 'chart-custom'}
      >
        {loading && <SpinDot className="position-abs" />}
        <ResponsiveContainer width={width} height={height}>
          <BarChart
            data={dataDash}
            margin={{
              top: 0,
              right: 50,
              left: -5,
              bottom: 10,
            }}
            barGap={12}
            barCategoryGap={120}
          >
            <CartesianGrid horizontal vertical={false} />
            <XAxis dataKey="name" />
            <YAxis type="number" ticks={yAxis} unit="%" />
            <Tooltip cursor={{ fill: 'transparent' }} />
            <Legend align="right" iconType="circle" />
            {bars.map(bar => (
              <Bar
                key={bar.year}
                unit="%"
                dataKey={bar.year}
                fill={bar.fill}
                barSize={32}
                radius={[4, 4, 4, 4]}
                label={customLabel}
              />
            ))}
          </BarChart>
        </ResponsiveContainer>
      </Row>
      <VerMais
        tipo={tipo}
        setDate={setDate}
        setVerMais={setIsVerMais}
        setOrder={setOrder}
        dataAlterada={dataAlterada}
        setDataAlterada={setDataAlterada}
        tab={tab}
      />
    </>
  );
}

AderenciaCarregamento.propTypes = {
  tipo: PropTypes.string.isRequired,
  datas: PropTypes.instanceOf(Object).isRequired,
  setDate: PropTypes.func.isRequired,
  tab: PropTypes.string.isRequired,
  width: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  height: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
};

AderenciaCarregamento.defaultProps = {
  width: '100%',
  height: '100%',
};

export default AderenciaCarregamento;
